import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Mail, Lock, User as UserIcon, Eye, EyeOff, Flame } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign In — Royal Lumina" },
      { name: "description", content: "Sign in or create your Royal Lumina account to track orders and manage your candle rituals." },
      { property: "og:title", content: "Sign In — Royal Lumina" },
      { property: "og:description", content: "Access your Royal Lumina account to track orders, save wishlists and manage your rituals." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const navigate = useNavigate();
  const redirect = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("redirect") || undefined : undefined;

  const onSubmit = async (e?: React.FormEvent | React.SyntheticEvent) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!email || !email.trim()) {
      toast.error("Email required", { description: "Please enter your email address to sign in." });
      return;
    }

    if (!password || !password.trim()) {
      toast.error("Password required", { description: "Please enter your password to sign in." });
      return;
    }

    setLoading(true);

    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password: password.trim(),
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
            data: {
              full_name: name,
            },
          },
        });

        if (error) throw error;

        toast.success("Account created successfully", { 
          description: "Enjoy your rituals at the atelier. Please verify your email if required." 
        });
        
        if (data.session) {
          navigate({ to: redirect || "/account" });
        } else {
          setMode("signin");
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password: password.trim(),
        });

        if (error) throw error;

        toast.success("Welcome back", { 
          description: "Enjoy your rituals at the atelier." 
        });
        navigate({ to: redirect || "/account" });
      }
    } catch (err: any) {
      console.error("Auth error:", err);
      const isRateLimit = err?.message?.toLowerCase().includes("rate limit");
      toast.error("Authentication failed", {
        description: isRateLimit
          ? "Supabase email rate limit reached (too many emails sent in 1 hour). Please wait 5-10 minutes or try signing in."
          : (err.message || "An unexpected error occurred.")
      });
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}${redirect || "/account"}`,
        }
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error("OAuth failed", { description: err.message });
    }
  };

  const onForgot = async () => {
    if (!email) {
      toast.error("Please enter your email first");
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      toast.success("Password reset link sent", { description: "Check your inbox." });
    } catch (err: any) {
      toast.error("Reset failed", { description: err.message });
    }
  };  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans">
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1 grid lg:grid-cols-2">
        <div className="relative hidden lg:block bg-primary text-cream overflow-hidden">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_20%,var(--gold)_0%,transparent_50%)]" />
          <div className="relative h-full flex flex-col p-16">
            <Link to="/" className="flex items-center gap-2 mb-28">
              <Flame className="h-7 w-7 text-gold" />
              <div className="font-display text-2xl">ROYAL <span className="text-gold">LUMINA</span></div>
            </Link>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold/80">Members Only</div>
              <h2 className="mt-4 font-display text-5xl leading-[1.05]">
                Rituals of <span className="italic text-gold">quiet luxury</span>, kept for you.
              </h2>
              <p className="mt-6 text-cream/70 text-sm max-w-md leading-relaxed font-light">
                Sign in to manage your candle subscriptions, track boutique shipments, and receive priority access to limited seasonal releases.
              </p>
            </div>
            <div className="mt-auto text-xs text-cream/40 tracking-widest uppercase">
              Atelier Member Portal — Royal Lumina
            </div>
          </div>
        </div>

        <div className="grid place-items-center px-6 py-16 lg:py-24">
          <div className="w-full max-w-md">
            <div className="text-center">
              <p className="eyebrow">{mode === "signin" ? "Welcome back" : "Join Royal Lumina"}</p>
              <h1 className="mt-3 font-display text-4xl md:text-5xl">
                {mode === "signin" ? <>Sign <span className="italic text-gold">In</span></> : <>Create <span className="italic text-gold">Account</span></>}
              </h1>
              <div className="mx-auto mt-5 h-px w-16 bg-gold" />
            </div>

            {/* Mode Switcher */}
            <div className="mt-8 grid grid-cols-2 rounded-full border border-border p-1 bg-card relative overflow-hidden">
              {(["signin", "signup"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`relative z-10 h-10 rounded-full text-sm tracking-wide transition-colors duration-300 ${
                    mode === m ? "text-cream" : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {m === "signin" ? "Sign In" : "Sign Up"}
                  {mode === m && (
                    <div className="absolute inset-0 bg-primary rounded-full -z-10 transition-all duration-300" />
                  )}
                </button>
              ))}
            </div>

            <form onSubmit={onSubmit} noValidate autoComplete="off" className="mt-8 space-y-4">
              {mode === "signup" && (
                <div>
                  <label htmlFor="auth-name" className="block text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">Full Name</label>
                  <div className="relative mt-2">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-20" strokeWidth={1.6} />
                    <input
                      id="auth-name"
                      name="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={name}
                      autoComplete="name"
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") onSubmit(e); }}
                      required
                      className="w-full h-12 pl-11 pr-4 bg-card border border-border rounded-[12px] text-sm text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:border-gold"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="auth-email" className="block text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">Email</label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-20" strokeWidth={1.6} />
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    autoComplete="username"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") onSubmit(e); }}
                    required
                    className="w-full h-12 pl-11 pr-4 bg-card border border-border rounded-[12px] text-sm text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:border-gold"
                  />
                </div>
              </div>
              
              <div className="space-y-0">
                <div className="flex items-center justify-between">
                  <label htmlFor="auth-password" className="block text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">Password</label>
                  {mode === "signin" && (
                    <button type="button" onClick={onForgot} className="text-xs text-gold hover:underline">Forgot password?</button>
                  )}
                </div>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-20" strokeWidth={1.6} />
                  <input
                    id="auth-password"
                    name="password"
                    type={showPw ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    autoComplete={mode === "signin" ? "current-password" : "new-password"}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") onSubmit(e); }}
                    required
                    className="w-full h-12 pl-11 pr-11 bg-card border border-border rounded-[12px] text-sm text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:border-gold"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold p-1 focus:outline-none z-20 cursor-pointer">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {mode === "signin" && (
                <div className="flex items-center text-xs pt-1">
                  <label htmlFor="auth-remember" className="inline-flex items-center gap-2 text-muted-foreground cursor-pointer select-none">
                    <input id="auth-remember" name="remember" type="checkbox" className="accent-[color:var(--gold)] rounded" /> Remember me
                  </label>
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-gold w-full justify-center h-12 disabled:opacity-70 font-semibold tracking-wider cursor-pointer">
                {loading ? "Please wait…" : mode === "signin" ? "SIGN IN" : "CREATE ACCOUNT"}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              {mode === "signin" ? "New to Royal Lumina?" : "Already a member?"}{" "}
              <button type="button" onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="text-gold hover:underline font-medium cursor-pointer">
                {mode === "signin" ? "Create an account" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
